<template>
    <div class="event__edit_cancellations">
        <form-checkbox
            :label="$t('ebs.events.edit.labels.learnersCanCancel')"
            :name="'cancel-booking-availability'"
            v-model="eventCancellationSelfAllowed"
        />
        <transition name="fade" mode="out-in">
            <form-number-radio-horizontal
                v-if="eventCancellationSelfAllowed"
                :label="$t('ebs.events.edit.labels.AllowBookingsCancel')"
                :after="$t('ebs.events.edit.labels.beforeSession')"
                :name="'cancel-booking-availability-before'"
                :items="[
                    {
                        name: this.$t('ebs.events.edit.name.hours'),
                        value: 'hour'
                    },
                    {
                        name: this.$t('ebs.events.edit.name.days'),
                        value: 'day'
                    }
                ]"
                :v-validate="'min_value:0'"
                :value-number="eventCancellationSelfLimit"
                :value-radio="eventCancellationSelfLimitType"
                @emit-value-number="updateEventCancellationSelfLimit"
                @emit-value-radio="updateEventCancellationSelfLimitType"
            />
        </transition>
        <component-title :size="'medium'">{{ $t('ebs.events.edit.title.cancellationReason') }}</component-title>
        <form-checkbox
            :label="$t('ebs.events.edit.labels.askForCancellationReason')"
            :name="'cancel-booking-reason'"
            :hint="$t('ebs.events.edit.labels.askLearners')"
            v-model="eventCancellationReasonRequired"
        />
        <transition-group name="fade" mode="out-in">
            <form-textarea
                :key="'cancel-booking-reason-items'"
                v-if="eventCancellationReasonRequired"
                :label="$t('ebs.events.edit.labels.cancellationReason')"
                :name="'cancel-booking-reason-items'"
                :data-vv-as="$t('forms.dataVvAs.cancellationReason')"
                :required="true"
                :hint="$t('ebs.events.edit.labels.addNewReason')"
                :v-validate="'required'"
                v-model="eventCancellationReason"
            >{{ $t('ebs.events.edit.title.addNewReason') }}
            </form-textarea>
            <form-checkbox
                :key="'allow-errors-reasons'"
                v-if="eventCancellationReasonRequired"
                :name="'allow-errors-reasons'"
                :label="`Allow 'Other' reasons`"
                v-model="eventCancellationReasonOther"
            />
        </transition-group>
        <component-title :size="'medium'">{{ $t('ebs.events.edit.title.automaticCancellations') }}</component-title>
        <form-checkbox
            :label="$t('ebs.events.edit.labels.automaticallyCancel')"
            :name="'cancel-booking-automatic'"
            v-model="eventCancellationAutoEnabled"
        />
        <transition name="fade" mode="out-in">
            <form-number-radio-horizontal
                v-if="eventCancellationAutoEnabled"
                :label="''"
                :after="$t('ebs.events.edit.name.beforeStart')"
                :name="'cancel-booking-automatic-before'"
                :items="[
                    {
                        name: this.$t('ebs.events.edit.name.hours'),
                        value: 'hour'
                    },
                    {
                        name: this.$t('ebs.events.edit.name.days'),
                        value: 'day'
                    }
                ]"
                :v-validate="'min_value:0'"
                :value-number="eventCancellationAutoLimit"
                :value-radio="eventCancellationAutoLimitType"
                @emit-value-number="updateEventCancellationAutoLimit"
                @emit-value-radio="updateEventCancellationAutoLimitType"
            />
        </transition>
        <form-submit
            :submit-button-animate-loading="!eventHasOccurrences"
            @reset="eventReset"
            @submit="beforeEventSubmit"
        />
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
        <pop-notify
            ref="popnotifynotaffectsession"
            :class="'pop__notify--notaffectsession'"
            :title-text="'Save changes?'"
            :content-text="'Your changes will not affect the existing sessions of this event.'"
            :cancel-button-text="$t('ebs.events.edit.notify.buttonClose')"
            :submit-button-text="$t('ebs.events.edit.notify.buttonConfirm')"
            :prevent-close="true"
            @emit-submit="eventSubmit"
        />
    </div>
</template>

<script>
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormNumberRadioHorizontal from '@/components/default/forms/FormNumberRadioHorizontal'
import FormTextarea from '@/components/default/forms/FormTextarea'
import FormSubmit from '@/components/default/forms/FormSubmit'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import { watchContentChangeEmit } from '@/helpers/mixins'

export default {
  components: {
    ComponentTitle,
    PopNotify,
    FormSubmit,
    FormTextarea,
    FormNumberRadioHorizontal,
    FormCheckbox
  },
  name: 'event-edit-cancellations',
  mixins: [watchContentChangeEmit],
  computed: {
    eventCancellationSelfAllowed: {
      get () {
        return this.$store.getters['getEventCancellationSelfAllowed']
      },
      set (value) {
        this.$store.dispatch('setEventCancellationSelfAllowed', value)
      }
    },
    eventCancellationSelfLimit: {
      get () {
        return this.$store.getters['getEventCancellationSelfLimit']
      }
    },
    eventCancellationSelfLimitType: {
      get () {
        return this.$store.getters['getEventCancellationSelfLimitType']
      }
    },
    eventCancellationReasonRequired: {
      get () {
        return this.$store.getters['getEventCancellationReasonRequired']
      },
      set (value) {
        this.$store.dispatch('setEventCancellationReasonRequired', value)
      }
    },
    eventCancellationReason: {
      get () {
        return this.$store.getters['getEventCancellationReason']
      },
      set (value) {
        this.$store.dispatch('setEventCancellationReason', value)
      }
    },
    eventCancellationReasonOther: {
      get () {
        return this.$store.getters['getEventCancellationReasonOther']
      },
      set (value) {
        this.$store.dispatch('setEventCancellationReasonOther', value)
      }
    },
    eventCancellationAutoEnabled: {
      get () {
        return this.$store.getters['getEventCancellationAutoEnabled']
      },
      set (value) {
        this.$store.dispatch('setEventCancellationAutoEnabled', value)
      }
    },
    eventCancellationAutoLimit: {
      get () {
        return this.$store.getters['getEventCancellationAutoLimit']
      }
    },
    eventCancellationAutoLimitType: {
      get () {
        return this.$store.getters['getEventCancellationAutoLimitType']
      }
    },
    watchContent () {
      return this.eventCancellationSelfAllowed + this.eventCancellationSelfLimit + this.eventCancellationSelfLimitType +
      this.eventCancellationReasonRequired + this.eventCancellationReason + this.eventCancellationReasonOther +
      this.eventCancellationAutoEnabled + this.eventCancellationAutoLimit + this.eventCancellationAutoLimitType
    },
    eventAllowEdit: function () {
      return this.$store.getters.getEventAllowEdit
    },
    eventAllowEditStatus: function () {
      return this.$store.getters.getEventAllowEditStatus
    },
    eventHasOccurrences () {
      return this.$store.getters.getEventHasOccurrences
    },
    eventCustomRequestLoaded () {
      return this.$store.getters.getEventCustomRequestLoaded
    }
  },
  methods: {
    validateBeforeRouteLeave: function (to) {
      if (to.name.includes('ebs-event-edit') || to.name.includes('ebs-event-add')) {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.$parent.validated = true
            this.$router.push({ path: to.path })
          } else {
            this.$refs.popnotifyvalidation.openNotify()
          }
        })
      } else {
        this.$parent.validated = true
        this.$router.push({ path: to.path })
      }
    },
    allowRedirectBeforeRouteLeave: function () {
      this.$eventBus.$emit('event-submit')
    },
    beforeEventSubmit () {
      if (this.eventHasOccurrences) {
        this.openNotifyNotAffectSessions()
      } else {
        this.eventSubmit()
      }
    },
    openNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.openNotify()
    },
    closeNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.closeNotify()
    },
    checkEventStatus (value) {
      if (value === 'active' && this.eventHasOccurrencesDraft) {
        this.$refs.popnotifyhasdraftsessions.openNotify()
      }
    },
    //
    updateEventCancellationSelfLimit: function () {
      this.$store.dispatch('setEventCancellationSelfLimit', event.target.value)
    },
    updateEventCancellationSelfLimitType: function () {
      this.$store.dispatch('setEventCancellationSelfLimitType', event.target.value)
    },
    updateEventCancellationAutoLimit: function () {
      this.$store.dispatch('setEventCancellationAutoLimit', event.target.value)
    },
    updateEventCancellationAutoLimitType: function () {
      this.$store.dispatch('setEventCancellationAutoLimitType', event.target.value)
    },
    //
    eventReset: function () {
      this.$store.dispatch('resetEvent')
    },
    eventSubmit: function () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.$route.name.includes('ebs-event-add')) {
            this.$store.dispatch('createEvent')
          } else if (this.$route.name.includes('ebs-event-edit')) {
            this.$store.dispatch('updateEvent')
          }
          this.allowRedirectBeforeRouteLeave()
        } else {
          this.$refs.popnotifyvalidation.openNotify()
        }
      })
    }
  },
  watch: {
    eventCustomRequestLoaded (value) {
      if (value) {
        this.closeNotifyNotAffectSessions()
      }
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>
